import React, { useState } from 'react';
import { useForm, SubmitHandler, FieldErrors } from 'react-hook-form';
import { useToast, FormControl, FormLabel, Input, Box, Text, Flex, Checkbox, Link as ChakraLink, Button, Link } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { phoneRegExp } from '../../Profile/Profile/ProfileFieldCard';
import { sendTrackingEvent } from '../../../../utils/gtm';

export interface SignUpFormInputs {
	name: string;
	email: string;
	phoneNumber: string;
	termsCheck: string;
}

interface ErrorResponse {
	message: string;
}
interface Props {
	register?: any;
	errors: FieldErrors<SignUpFormInputs>;
	isSignUpChecked: boolean;
	setIsSignUpChecked: React.Dispatch<React.SetStateAction<boolean>>;
}

const SignUpForm = ({ register, errors, isSignUpChecked, setIsSignUpChecked }: Props) => {
	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

	const validatePhoneNumber = (value: string) => {
		const phoneRegex = /^[0-9]{10}$/;
		if (value.includes('+91')) {
			setPhoneNumberError('Phone number should not contain +91 and should be 10 digit long');
		} else if (!phoneRegex.test(value)) {
			setPhoneNumberError('Invalid phone number');
		} else {
			setPhoneNumberError(null);
		}
	};

	const nameRegex = /^[a-zA-Z]+(?: [a-zA-Z]+)*$/;
	const emailRegex =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

	const termsOfServiceLink = 'https://www.prepleaf.com/terms_&_conditions';
	const privacyPolicyLink = 'https://www.prepleaf.com/privacy_policy';

	return (
		<Box>
			<form
				onSubmit={e => {
					e.preventDefault();
				}}
			>
				<FormControl id="name" mb="16px" isInvalid={!!errors.name}>
					<Text as={FormLabel} textStyle="body2-md">
						Full Name
					</Text>
					<Input
						placeholder="Enter full name"
						type="text"
						{...register('name', {
							required: 'Name is required',
							pattern: {
								value: nameRegex,
								message: 'Invalid name format',
							},
						})}
					/>
					{errors.name && <Text color="#D61E27">{errors.name.message}</Text>}
				</FormControl>
				<FormControl id="email" mb="16px" isInvalid={!!errors.email}>
					<Text as={FormLabel} textStyle="body2-md">
						College Email Address
					</Text>
					<Input
						placeholder="Enter college email address"
						type="email"
						{...register('email', {
							required: 'Email is required',
							pattern: {
								value: emailRegex,
								message: 'Please enter a valid email address',
							},
                            setValueAs : (value:string) => value.toLowerCase()
						})} 
					/>
					{errors.email && <Text color="#D61E27">{errors.email.message}</Text>}
				</FormControl>
				<FormControl id="phone" mb="16px" isInvalid={!!errors?.phoneNumber || !!phoneNumberError}>
					<Text as={FormLabel} textStyle="body2-md">
						Phone Number
					</Text>
					<Input
						placeholder="Enter whatsapp number"
						type="tel"
						{...register('phoneNumber', {
							required: 'Invalid phone number',
							pattern: {
								value: phoneRegExp,
								message: 'Please enter a valid phone number',
							},
						})}
						// onChange={e => {
						// 	setPhoneNumber(e.target.value);
						// 	validatePhoneNumber(e.target.value);
						// }}
					/>
					{phoneNumberError && (
						<Text mt="6px" color="#D61E27">
							{phoneNumberError}
						</Text>
					)}
					{errors.phoneNumber && <Text color="#D61E27">{errors.phoneNumber.message}</Text>}
				</FormControl>
				<Flex pb="5">
					<Checkbox isInvalid={errors.termsCheck} name="termsCheck" {...register('termsCheck', { require: true })} size="md" mr="2"></Checkbox>
					<Text textStyle="caption">
						By signing up, I accept the Prepleaf{' '}
						<ChakraLink as={RouterLink} color="#386AFA" to={termsOfServiceLink} textDecor="underline" target="_blank">
							Terms of Service
						</ChakraLink>{' '}
						and acknowledge the{' '}
						<ChakraLink as={RouterLink} color="#386AFA" to={privacyPolicyLink} textDecor="underline" target="_blank">
							Privacy Policy.
						</ChakraLink>
					</Text>
				</Flex>

				<Box
					display={{ base: 'flex', md: 'none' }}
					gap="8px"
					color="#544D4F"
					textStyle="body1-md"
					justifyContent="center"
					textAlign="center"
					mt="10px"
					flexWrap="wrap"
				>
					Already have an account?
					<Link
						onClick={() => {
							sendTrackingEvent({
								event: 'cn_signup_signin',
								eventLabel: window.location.href,
							});
						}}
						as={RouterLink}
						color="#386AFA"
						to="/sign-in"
					>
						Sign In
					</Link>
				</Box>
			</form>
		</Box>
	);
};

export default SignUpForm;
